// Phase 9 R2 — Read-only transaction history viewer.
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useInventoryItemTransactions } from '@/hooks/inventory/useInventoryItemTransactions';
import type { InventoryItem } from '@/hooks/inventory/useInventoryItems';

const TYPE_LABEL: Record<string, string> = {
  initial_load: 'Initial Load',
  restock: 'Restock',
  manual_adjustment: 'Manual Adjustment',
  reservation: 'Reservation',
  release: 'Release',
  consumption: 'Consumption',
  return_to_stock: 'Return to Stock',
};

export function TransactionHistoryDialog({
  item,
  open,
  onOpenChange,
}: {
  item: InventoryItem | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data: txns = [], isLoading } = useInventoryItemTransactions(item?.id ?? null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Transaction History</DialogTitle>
          <DialogDescription>
            {item ? `${item.part_number} — ${item.name}` : ''} (read-only)
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[60vh] pr-3">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : txns.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No transactions yet.
            </div>
          ) : (
            <ul className="space-y-2">
              {txns.map((t) => {
                const positive = (t.quantity_delta ?? 0) > 0;
                const deltaSign = positive ? '+' : '';
                return (
                  <li key={t.id} className="rounded-md border p-3 text-sm">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {TYPE_LABEL[t.transaction_type as string] ?? t.transaction_type}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(t.created_at).toLocaleString()}
                        </span>
                      </div>
                      <span
                        className={
                          positive ? 'font-mono text-success' : 'font-mono text-destructive'
                        }
                      >
                        {deltaSign}{t.quantity_delta}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                      <div>
                        On Hand: <span className="font-mono text-foreground">{t.quantity_before}</span> → <span className="font-mono text-foreground">{t.quantity_after}</span>
                      </div>
                      <div>
                        Reserved: <span className="font-mono text-foreground">{t.reserved_before}</span> → <span className="font-mono text-foreground">{t.reserved_after}</span>
                      </div>
                      {t.ticket_id && <div>Ticket: <span className="font-mono">{t.ticket_id}</span></div>}
                      {t.parts_line_id && <div>Parts Line: <span className="font-mono">{String(t.parts_line_id).slice(0, 8)}…</span></div>}
                    </div>
                    {t.notes && (
                      <div className="mt-2 text-sm">{t.notes}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
